import { voiceLeadPiano } from '@/music/pianoComping';
// Piano Voicings (#236): the instrument seam made visible. A ii–V–I is
// voice-led over PIANO close-position candidates using the pitch metric
// alone — the same greedy argmin the seam-proof test runs — and drawn on
// keyboard strips: pressed keys dotted, the held-common-tone COUNT and the
// movement bill in each caption beside a root-position baseline. No fret
// appears anywhere in the computation, which is the exhibit's whole point.

import { useMemo, useState } from 'react';
import { getChordPlayer } from '@/audio/player';
import { instrumentById, type InstrumentVoicing } from '@/music/instruments';
import type { PianoFamily } from '@/music/pianoVoicings';
import { pitchDistanceBetweenMidis } from '@/music/voiceLeading';
import { parseKey, pitchClassOf, sharpName, spellNoteInKey } from '@/music/notes';
import { spelledOctave } from '@/music/chordSpelling';
import type { ChordTypeId } from '@/music/chords';
import type { Inversion } from '@/music/voicing';
import { PianoKeyboardDiagram } from '@/components/PianoKeyboardDiagram';
import { keyboardRange } from '@/components/pianoKeyboardGeometry';

const KEYS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
/** Octave-tagged name for a pitch, using the candidate's own diatonic
 * spelling (chordSpelling.ts) rather than re-deriving a sharp name — an
 * E♭7's ♭7 must read D♭ here exactly as it does everywhere else, and the
 * octave follows the letter (B3 spelled C♭ is C♭4). */
const noteNames = (midis: number[], voicing: string[], all: number[]) =>
  midis.map((midi) => {
    const spelled = voicing[all.indexOf(midi)] ?? sharpName(((midi % 12) + 12) % 12);
    return `${spelled}${spelledOctave(midi, spelled)}`;
  });
const FAMILIES: { id: PianoFamily; label: string; blurb: string; span?: number }[] = [
  {
    id: 'mantooth',
    label: 'Mantooth',
    blurb:
      'generic, Miracle and polychord shapes; RH thumb normally C4–C5, with two supporting LH voices',
  },
  {
    id: 'guide-tones',
    label: 'Guide tones',
    blurb: 'the 3rd and 7th only',
  },
  {
    id: 'guide-tones-root',
    label: 'Guide tones + root',
    blurb: 'the root in the left hand under the 3rd and 7th',
  },
  { id: 'close', label: 'Close', blurb: 'four notes in close position' },
  // Drop 2 spans a 10th and up: a two-hand family, so the hand rule widens.
  { id: 'drop-2', label: 'Drop 2', blurb: 'drop 2 played with two hands', span: 19 },
  { id: 'shell', label: 'Shells', blurb: 'compact R-3-7 inversions' },
  { id: 'rootless', label: 'Rootless', blurb: 'Evans and Kelly A and B forms, without the root' },
  {
    id: 'two-hand',
    label: 'Two hands',
    blurb: 'the root in the left hand under a rootless voicing',
  },
  {
    id: 'upper-structure',
    label: 'Upper structures',
    blurb: 'a left-hand shell (R-3-7) under a right-hand triad of tensions, after Levine',
  },
  {
    id: 'quartal',
    label: 'Quartal',
    blurb: 'stacked fourths, with a tritone in the left hand on dominants',
  },
];
const INVERSION_LABEL = ['Root position', '1st inversion', '2nd inversion', '3rd inversion'];
const ROOTLESS_LABEL = ['A form', 'rotation 1', 'B form', 'rotation 3'];
const INVERSIONS: Inversion[] = [0, 1, 2, 3];

interface Step {
  candidate: InstrumentVoicing;
  held: number[];
  moved: number;
}

function candidatesFor(root: string, type: ChordTypeId, family: PianoFamily): InstrumentVoicing[] {
  const piano = instrumentById('piano');
  const span = FAMILIES.find((f) => f.id === family)?.span;
  return INVERSIONS.flatMap((inversion) =>
    piano.enumerate(root, type, inversion, family, {
      ...(span ? { maxHandSpan: span } : {}),
      // The root is already written the way the key spells it, so it doubles
      // as the spelling source for the voicing's note names.
      displayRoot: root,
    }),
  );
}

/** Greedy pitch walk: anchor the ii near middle C, then nearest-candidate. */
function walk(changes: [string, ChordTypeId][], family: PianoFamily): Step[] {
  if (family === 'mantooth') {
    // The teaching example uses the same phrase search and register priorities
    // as workbench playback, rather than anchoring the LH near middle C.
    const line = voiceLeadPiano(
      changes.map(([root, chordType]) => ({ root, chordType })),
      { family },
    )!;
    return line.map((chord, i) => ({
      candidate: candidatesFor(chord.root, chord.chordType, family).find(
        (c) => c.midis.join() === chord.midis.join(),
      )!,
      held: i ? chord.midis.filter((m) => line[i - 1].midis.includes(m)) : [],
      moved: chord.distance ?? 0,
    }));
  }
  const first = candidatesFor(...changes[0], family).reduce((best, c) =>
    Math.abs(c.midis[0] - 60) < Math.abs(best.midis[0] - 60) ? c : best,
  );
  const steps: Step[] = [{ candidate: first, held: [], moved: 0 }];
  for (const change of changes.slice(1)) {
    const previous = steps[steps.length - 1].candidate;
    const next = candidatesFor(...change, family).reduce((best, c) =>
      pitchDistanceBetweenMidis(previous.midis, c.midis) <
      pitchDistanceBetweenMidis(previous.midis, best.midis)
        ? c
        : best,
    );
    steps.push({
      candidate: next,
      held: next.midis.filter((m) => previous.midis.includes(m)),
      moved: pitchDistanceBetweenMidis(previous.midis, next.midis),
    });
  }
  return steps;
}

export function PianoVoicingsExhibit() {
  const [keyRoot, setKeyRoot] = useState('C');
  const [family, setFamily] = useState<PianoFamily>('rootless');

  const { steps, baseline, range } = useMemo(() => {
    const pc = pitchClassOf(keyRoot);
    // Name the ii and the V the way the KEY would write them: in E♭ the V is
    // B♭, not A♯, and the voicings are spelled from these roots.
    const key = parseKey(keyRoot);
    const changes: [string, ChordTypeId][] = [
      [spellNoteInKey((pc + 2) % 12, key), 'min7'],
      [spellNoteInKey((pc + 7) % 12, key), 'dom7'],
      [keyRoot, 'maj7'],
    ];
    const led = walk(changes, family);
    const roots = changes.map(
      (change) => candidatesFor(...change, family).find((c) => c.inversion === 0)!,
    );
    const baselineWork = roots
      .slice(1)
      .reduce((sum, c, i) => sum + pitchDistanceBetweenMidis(roots[i].midis, c.midis), 0);
    const all = led.flatMap((s) => s.candidate.midis);
    return {
      steps: led,
      baseline: baselineWork,
      range: keyboardRange(all),
    };
  }, [keyRoot, family]);

  const total = steps.reduce((sum, s) => sum + s.moved, 0);

  const play = (midis: number[]) => {
    try {
      // The struck-piano voice, with a pianist's slight roll up the chord.
      void getChordPlayer().playMidi(midis, 0.012, 'piano');
    } catch {
      /* no audio (e.g. tests) */
    }
  };
  const playLine = () => {
    steps.forEach((s, i) => setTimeout(() => play(s.candidate.midis), i * 900));
  };

  return (
    <div data-testid="piano-voicings-exhibit">
      <p className="lab-lede">
        A ii–V–I voice-led over <strong>piano</strong> voicings by pitch distance alone, with no
        frets or string sets involved. Tap a keyboard to hear it.
      </p>
      <div className="lab-controls" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <label>
          Key{' '}
          <select
            value={keyRoot}
            onChange={(e) => setKeyRoot(e.target.value)}
            data-testid="piano-voicings-key"
          >
            {KEYS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label>
          Family{' '}
          <select
            value={family}
            onChange={(e) => setFamily(e.target.value as PianoFamily)}
            data-testid="piano-voicings-family"
          >
            {FAMILIES.map((f) => (
              <option key={f.id} value={f.id} title={f.blurb}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-sm"
          onClick={playLine}
          data-testid="piano-voicings-play"
        >
          ▶ Play the line
        </button>
        <span className="muted" data-testid="piano-voicings-movement">
          {total} semitones of movement · root-position restating costs {baseline}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginTop: 14,
        }}
      >
        {steps.map((step, i) => (
          <figure key={i} style={{ margin: 0 }}>
            <PianoKeyboardDiagram
              midis={step.candidate.midis}
              low={range.low}
              high={range.high}
              // Two-hand textures span three-plus octaves; give the strip room.
              width={
                (
                  [
                    'two-hand',
                    'upper-structure',
                    'quartal',
                    'guide-tones-root',
                    'mantooth',
                  ] as PianoFamily[]
                ).includes(family)
                  ? 300
                  : 230
              }
              label={step.candidate.symbol}
              onClick={() => play(step.candidate.midis)}
            />
            <figcaption style={{ fontSize: 13, marginTop: 4 }}>
              <strong>{step.candidate.symbol}</strong> ·{' '}
              {family === 'mantooth'
                ? 'Mantooth construction'
                : family === 'quartal'
                  ? 'stacked fourths'
                  : family === 'guide-tones' || family === 'guide-tones-root'
                    ? ['3rd on the bottom', '7th on the bottom'][step.candidate.inversion]
                    : (family === 'rootless' || family === 'two-hand'
                        ? ROOTLESS_LABEL
                        : INVERSION_LABEL)[step.candidate.inversion]}
              {i > 0 && (
                <span className="muted">
                  {' '}
                  · {step.held.length} held, {step.moved} st moved
                </span>
              )}
              {step.candidate.upperTriad && (
                <span style={{ display: 'block' }} data-testid="upper-triad">
                  <strong>
                    {step.candidate.upperTriad.root}
                    {step.candidate.upperTriad.quality === 'minor' ? 'm' : ''} /{' '}
                    {step.candidate.symbol}
                  </strong>
                </span>
              )}
              {step.candidate.hands && (
                <span className="muted" style={{ display: 'block' }}>
                  LH{' '}
                  {noteNames(
                    step.candidate.hands.left,
                    step.candidate.voicing,
                    step.candidate.midis,
                  ).join(' ')}{' '}
                  · RH{' '}
                  {noteNames(
                    step.candidate.hands.right,
                    step.candidate.voicing,
                    step.candidate.midis,
                  ).join(' ')}
                </span>
              )}
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>
        The voicings come from the same registry as the guitar grips. Each is a stack of pitches
        limited by hand span, where a guitar voicing is limited by fret span. The workbench voice
        leading uses the same search.
      </p>
    </div>
  );
}
